import { type MouseEvent, useState } from "react";
import { formatMonthShort } from "@/plan-logic/lib/dates";
import { money } from "@/plan-logic/lib/format";
import type { MonthFlow } from "@/plan-logic/plan-view";
import { niceAxis, tickText } from "./scale";
import "@/styles/v47-pages.css";

/**
 * The prototype's "Money in versus money out" bars (`drawBars`), from the plan's months. The month
 * under the pointer lights (v44: gold, on a bar or in the gap between the pair); nothing dims.
 */
export function MonthBars({ rows }: { rows: MonthFlow[] }) {
  const [on, setOn] = useState<number | null>(null);
  if (!rows.length) return <p className="mut">No months to show yet.</p>;
  const data = rows.map((r) => ({ ...r, diff: r.moneyIn - r.moneyOut }));
  const axis = niceAxis(0, Math.max(...data.map((d) => Math.max(d.moneyIn, d.moneyOut))) * 1.12);
  const max = axis.hi;
  const W = 980;
  const H = 340;
  const L = 64;
  const B = 52;
  const T = 16;
  const R = 12;
  const step = (W - L - R) / data.length;
  const bw = Math.min(46, step / 2.6);
  const gap = 6;
  const Y = (v: number) => H - B - (v / max) * (H - T - B);
  const neg = data.filter((d) => d.diff < 0).length;
  const pick = (ev: MouseEvent<SVGSVGElement>) => {
    const r = ev.currentTarget.getBoundingClientRect();
    if (!r.width) return;
    const k = Math.floor((((ev.clientX - r.left) / r.width) * W - L) / step);
    setOn(k >= 0 && k < data.length ? k : null);
  };
  return (
    <>
      <div className="legend" style={{ marginBottom: 8 }}>
        <span>
          <i className="lgd" style={{ background: "var(--ess)" }} />
          Money in
        </span>
        <span>
          <i className="lgd" style={{ background: "var(--save)" }} />
          What you kept
        </span>
        <span>
          <i className="lgd" style={{ background: "var(--life)" }} />
          Money out
        </span>
        <span>
          <i className="lgd" style={{ background: "var(--debt)" }} />
          More than came in
        </span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="bars cfbars"
        role="img"
        aria-label="Money in against money out by month"
        onMouseMove={pick}
        onMouseLeave={() => setOn(null)}
      >
        {axis.ticks.map((t) => (
          <g key={t}>
            <line x1={L} x2={W - R} y1={Y(t)} y2={Y(t)} stroke="var(--line2)" />
            <text x={L - 9} y={Y(t) + 4} textAnchor="end" className="bx">
              {tickText(t, axis.step)}
            </text>
          </g>
        ))}
        {data.map((_, k) => (
          <rect
            key={`c${k}`}
            className={`cfcol${on === k ? " on" : ""}`}
            data-k={k}
            x={(L + step * k).toFixed(1)}
            y={T}
            width={step.toFixed(1)}
            height={H - B - T}
          />
        ))}
        {data.map((d, k) => {
          const cx = L + step * k + step / 2;
          const xi = cx - bw - gap / 2;
          const xo = cx + gap / 2;
          const surplus = Math.max(0, d.diff);
          const deficit = Math.max(0, -d.diff);
          const inBase = d.moneyIn - surplus;
          const outBase = d.moneyOut - deficit;
          const label = formatMonthShort(d.month);
          return (
            <g key={k}>
              <rect
                x={xi}
                y={Y(inBase)}
                width={bw}
                height={H - B - Y(inBase)}
                fill="var(--ess)"
                rx="3"
              >
                <title>{`${label}: money in ${money(d.moneyIn)}`}</title>
              </rect>
              {surplus > 0 && (
                <rect
                  x={xi}
                  y={Y(d.moneyIn)}
                  width={bw}
                  height={Y(inBase) - Y(d.moneyIn)}
                  fill="var(--save)"
                  rx="3"
                >
                  <title>{`${label}: kept ${money(surplus)}`}</title>
                </rect>
              )}
              <rect
                x={xo}
                y={Y(outBase)}
                width={bw}
                height={H - B - Y(outBase)}
                fill="var(--life)"
                rx="3"
              >
                <title>{`${label}: money out ${money(d.moneyOut)}`}</title>
              </rect>
              {deficit > 0 && (
                <rect
                  x={xo}
                  y={Y(d.moneyOut)}
                  width={bw}
                  height={Y(outBase) - Y(d.moneyOut)}
                  fill="var(--debt)"
                  rx="3"
                >
                  <title>{`${label}: ${money(deficit)} more than came in`}</title>
                </rect>
              )}
              <text x={cx} y={H - 30} textAnchor="middle" className="bx">
                {label.replace(" ", " ")}
              </text>
              <text
                x={cx}
                y={H - 13}
                textAnchor="middle"
                className="bd"
                fill={d.diff < 0 ? "var(--debt)" : "var(--ok)"}
              >
                {d.diff < 0 ? "-" : "+"}
                {money(Math.abs(d.diff))}
              </text>
            </g>
          );
        })}
      </svg>
      <p className="hint">
        {neg
          ? `${neg} of these ${data.length} months cost more than comes in.`
          : "Every month in this window comes in under what you earn."}{" "}
        Savings and investments count as kept, not as money out.
      </p>
    </>
  );
}
